import { durationLabel, parseWall, wallDay, wallDayLabel, wallTime } from 'utils/shortTerm'
import { getWarehouses } from 'containers/Warehouses/warehousesSlice'
import { selectUserId } from 'containers/Login/loginSlice'
import { useReduxDispatch, useReduxSelector } from 'store'
import React, { useEffect, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import { useHistory } from 'react-router-dom'
import { displayPrice } from 'utils/string'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'
import './shortTermExtension.scss'

interface ExtensionResult {
  to?: string
  price?: { gross: string, net: string }
}

/**
 * Przedłużenie umowy krótkoterminowej (kontrakt dla frontu §4): warianty z rentedreserved.extendOptions,
 * wysyłane jako { extendMinutes }. Dopłata = dokument wystawiony od razu, płacony zwykłą ścieżką faktur.
 */
const ShortTermExtension: React.FC<{ warehouse: warehouse.Warehouse }> = ({ warehouse }) => {
  const { contractId, objectId, fullDateTo, extendOptions = [] } = warehouse
  const userId = useReduxSelector(selectUserId)

  const [selected, setSelected] = useState<number | undefined>(() => extendOptions.find(o => o.available)?.minutes)
  const [isSending, setSending] = useState(false)
  const [error, setError] = useState<string>()
  const [result, setResult] = useState<ExtensionResult>()

  const dispatch = useReduxDispatch()
  const { goBack, push } = useHistory()
  const { t, lang } = useTranslation('warehouse.shortTerm.extend')

  // warianty odświeżają się z rentedreserved (socket) — gdy wybrany przestał być dostępny, bierzemy pierwszy dostępny
  useEffect(() => {
    if (!extendOptions.some(o => o.minutes === selected && o.available)) {
      setSelected(extendOptions.find(o => o.available)?.minutes)
    }
  }, [extendOptions, selected])

  const currentEnd = parseWall(fullDateTo)
  const option = extendOptions.find(o => o.minutes === selected && o.available)
  const fullDate = (m: number) => `${wallDayLabel(m, lang, { weekday: 'short', day: '2-digit', month: '2-digit' })}, ${wallTime(m)}`
  /** sama godzina, gdy nowy zwrot wypada tego samego dnia */
  const returnLabel = (value: string) => {
    const end = parseWall(value)
    if (end === undefined) return value
    return currentEnd !== undefined && wallDay(end) === wallDay(currentEnd) ? wallTime(end) : fullDate(end)
  }

  const send = async () => {
    if (!option || isSending) return
    setSending(true)
    setError(undefined)
    try {
      const response = await POST(urls.contractExtend(contractId), { body: { extendMinutes: option.minutes } }, false)
      if (response?.result !== 'ok') throw response
      dispatch(getWarehouses(userId))
      setResult({ to: response?.extension?.to || option.endsAt, price: response?.extension?.price || option.price })
    } catch (e) {
      setError(getErrorMessage(e) || t('error'))
    } finally {
      setSending(false)
    }
  }

  if (result) {
    const end = parseWall(result.to)
    return (
      <div className='menu__section menu__extension stExtension'>
        <div className='title'>
          <div>{t('success')}</div>
          {end !== undefined && <div>{t('successDesc').replace('{date}', fullDate(end))}</div>}
        </div>
        <div className='extension__main'>
          {result.price && <div className='stExtension__summary'>
            <span>{t('toPay')}</span>
            <b>{displayPrice(result.price.gross)}</b>
          </div>}
          <div className='extension__buttons'>
            <ArrowBtn
              enlarged
              text={t('pay')}
              action={() => push(`/warehouses/${objectId}/invoices?selectAll=1`)}
            />
            <BackBtn
              text={t('back')}
              action={goBack}
            />
          </div>
        </div>
      </div>
    )
  }

  const nothingAvailable = extendOptions.length > 0 && !extendOptions.some(o => o.available)

  return (
    <div className='menu__section menu__extension stExtension'>
      <div className='title'>
        <div>{t('title')}</div>
        <div>{t('subtitle')}</div>
      </div>

      <div className='extension__main'>
        {currentEnd !== undefined && <div className='stExtension__summary'>
          <span>{t('currentReturn')}</span>
          <b>{fullDate(currentEnd)}</b>
        </div>}

        {extendOptions.length === 0
          ? <div className='stExtension__message'>{t('noOptions')}</div>
          : <div className='stExtension__options'>
            {extendOptions.map(o => <button
              key={o.minutes}
              type='button'
              disabled={!o.available}
              className={`stExtension__option${o.minutes === selected && o.available ? ' --selected' : ''}`}
              onClick={() => {
                setSelected(o.minutes)
                setError(undefined)
              }}>
              <div className='stExtension__option__main'>
                <b className='--secFont'>+{durationLabel(o.minutes, lang)}</b>
                <span>{o.available
                  ? t('newReturn').replace('{time}', returnLabel(o.endsAt))
                  : t(`reasons.${o.reason || 'default'}`)}</span>
              </div>
              <div className='stExtension__option__price'>{displayPrice(o.price.gross)}</div>
            </button>)}
          </div>}

        {nothingAvailable && <div className='stExtension__message'>{t('allUnavailable')}</div>}
        {error && <div className='stExtension__message --error'>{error}</div>}
        {option && <div className='stExtension__info'>{t('paymentInfo')}</div>}

        <div className='extension__buttons'>
          <ArrowBtn
            enlarged
            disabled={!option}
            isLoading={isSending}
            text={option ? t('extendFor').replace('{price}', displayPrice(option.price.gross)) : t('title')}
            action={send}
          />
          <BackBtn />
        </div>
      </div>
    </div>
  )
}

/** komunikat z odpowiedzi extend (errors[] / message), także z odrzuconego POST (JSON w stringu) */
const getErrorMessage = (error: unknown): string | undefined => {
  let data: any = error
  if (typeof error === 'string') {
    try {
      data = JSON.parse(error)
    } catch {
      return
    }
  }
  if (!data || typeof data !== 'object') return
  const errors = Array.isArray(data.errors)
    ? data.errors
    : data.errors && typeof data.errors === 'object' ? Object.values(data.errors).flat() : []
  const text = errors.find((e: unknown) => typeof e === 'string')
  return text || (typeof data.message === 'string' ? data.message : undefined)
}

export default ShortTermExtension
